import { useRequiredContext } from "@redotech/react-util/context";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { returnsSettingsParentPath } from "@redotech/redo-merchant-app-common/settings/returns-settings-parent-path";
import { useReturnsSettingsExperience } from "@redotech/redo-merchant-app-common/settings/use-returns-settings-experience";
import { BreadcrumbOverridersContext } from "@redotech/redo-web/breadcrumb";
import { memo, useContext, useEffect } from "react";
import { Outlet } from "react-router-dom";

/**
 * Route layout for an old returns page's sub-pages. While the merchant uses the
 * new returns settings page, the old page's breadcrumb points at the tab that
 * replaced it, so there is a way back from pages without a Back button.
 */
export const ReturnsSettingsParentBreadcrumb = memo(
  function ReturnsSettingsParentBreadcrumb({
    legacyPath,
  }: {
    legacyPath: string;
  }) {
    const routes = useRequiredContext(MerchantRoutesContext);
    const { useNewUi } = useReturnsSettingsExperience();
    const [, setBreadcrumbOverriders] = useContext(BreadcrumbOverridersContext);
    const legacyUrl = routes.getSettingsSubRoute(legacyPath);
    const parentUrl = routes.getSettingsSubRoute(
      returnsSettingsParentPath(legacyPath, useNewUi),
    );

    useEffect(() => {
      if (legacyUrl === parentUrl) {
        return;
      }
      const key = `ReturnsSettingsParentBreadcrumb:${legacyPath}`;
      setBreadcrumbOverriders((previous) =>
        new Map(previous).set(key, (breadcrumb) =>
          breadcrumb.url === legacyUrl
            ? { ...breadcrumb, url: parentUrl }
            : breadcrumb,
        ),
      );
      return () =>
        setBreadcrumbOverriders((previous) => {
          const next = new Map(previous);
          next.delete(key);
          return next;
        });
    }, [legacyPath, legacyUrl, parentUrl, setBreadcrumbOverriders]);

    return <Outlet />;
  },
);
